import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight, CheckCircle } from "lucide-react";
import { submitQuestionnaire, WorkflowApiError } from "../services/workflow";

type FormData = {
  websiteExists: string;
  websiteUrl: string;
  improve: string[];
  improveOther: string;
  goal: string[];
  goalOther: string;
  successMetrics: string[];
  successMetricsOther: string;
  customers: string;
  customersSpecific: string;
  ageGroup: string;
  knowledge: string[];
  importantPages: string[];
  pagesOther: string;
  needsExplaining: string;
  explainingWhat: string;
  assetsAvailability: string;
  impact: string[];
  branding: string[];
  positiveExamples: string;
  negativeExamples: string;
  seoTerms: string;
  mostImportantService: string;
  region: string;
  functionsNeeded: string[];
  functionsOther: string;
  notes: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contactCompany: string;
  privacy: boolean;
};

const steps = [
  { title: "Ausgangslage", hint: "Wo stehen Sie heute?" },
  { title: "Ziel & Erwartung", hint: "Woran messen wir Erfolg?" },
  { title: "Zielgruppe", hint: "Für wen ist die Website?" },
  { title: "Inhalte & Struktur", hint: "Welche Seiten & Inhalte?" },
  { title: "Design & Wirkung", hint: "Wie soll es sich anfühlen?" },
  { title: "SEO", hint: "Wie sollen Sie gefunden werden?" },
  { title: "Funktionen", hint: "Was muss die Website können?" },
  { title: "Abschluss", hint: "Letzte Hinweise" },
];

const improveOptions = [
  "Moderneres Design",
  "Bessere Übersicht / Struktur",
  "Mehr Anfragen",
  "Bessere Auffindbarkeit bei Google",
  "Zusätzliche/weitere Inhalte",
  "Inhalte aktualisieren",
  "Technische Probleme beheben",
];

const goalOptions = [
  "Neue Kunden gewinnen",
  "Vertrauen aufbauen",
  "Leistungen verständlich erklären",
  "Kontaktanfragen erhalten",
  "Produkte / Leistungen verkaufen",
  "Informieren",
  "Sonstiges",
];

const successOptions = [
  "Mehr Anfragen",
  "Qualitativ bessere Anfragen",
  "Positive Rückmeldungen",
  "Bessere Google-Platzierung",
];

const ageOptions = ["unter 25", "25–40", "40–60", "60+"];

const knowledgeOptions = [
  "Kaum / Einsteiger",
  "Grundwissen",
  "Sehr gut / Fachlich",
];

const pageOptions = [
  "Startseite",
  "Über uns",
  "Leistungen / Angebote",
  "Preise",
  "Referenzen / Bewertungen",
  "Kontakt",
  "Blog / Aktuelles",
];

const impactOptions = [
  "Seriös & vertrauenswürdig",
  "Modern & klar",
  "Persönlich & nahbar",
  "Kreativ & auffällig",
  "Schlicht & ruhig",
  "Hochwertig / exklusiv",
];

const functionOptions = [
  "Kontaktformular",
  "Terminbuchung",
  "Newsletter",
  "Download-Bereich",
  "Mehrsprachigkeit",
  "Online-Shop",
];

const inputClassName =
  "w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 outline-none transition focus:border-gray-400 focus:ring-2 focus:ring-gray-200";
const textareaClassName = `${inputClassName} resize-none`;
const cardClassName =
  "rounded-3xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8";
const FORM_COOKIE_NAME = "mutzur_fragebogen_v1";
const FORM_SUBMITTED_COOKIE_NAME = "mutzur_fragebogen_submitted_v1";
const FORM_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const defaultFormValues: FormData = {
  websiteExists: "",
  websiteUrl: "",
  improve: [],
  improveOther: "",
  goal: [],
  goalOther: "",
  successMetrics: [],
  successMetricsOther: "",
  customers: "",
  customersSpecific: "",
  ageGroup: "",
  knowledge: [],
  importantPages: [],
  pagesOther: "",
  needsExplaining: "",
  explainingWhat: "",
  assetsAvailability: "",
  impact: [],
  branding: [],
  positiveExamples: "",
  negativeExamples: "",
  seoTerms: "",
  mostImportantService: "",
  region: "",
  functionsNeeded: [],
  functionsOther: "",
  notes: "",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  contactCompany: "",
  privacy: false,
};

type PersistedFormState = {
  currentStep: number;
  values: Partial<FormData>;
};

type PersistedSubmissionState = {
  submittedAt: string;
  submittedEmail: string;
};

function getCookieValue(name: string) {
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${name}=`));

  if (!cookie) {
    return null;
  }

  return cookie.slice(name.length + 1);
}

function readPersistedFormState(): PersistedFormState | null {
  const cookieValue = getCookieValue(FORM_COOKIE_NAME);
  if (!cookieValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      decodeURIComponent(cookieValue),
    ) as PersistedFormState;

    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.currentStep !== "number" ||
      typeof parsed.values !== "object" ||
      parsed.values === null
    ) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function readPersistedSubmissionState(): PersistedSubmissionState | null {
  const cookieValue = getCookieValue(FORM_SUBMITTED_COOKIE_NAME);
  if (!cookieValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      decodeURIComponent(cookieValue),
    ) as PersistedSubmissionState;

    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof parsed.submittedAt !== "string" ||
      typeof parsed.submittedEmail !== "string" ||
      parsed.submittedEmail.trim() === ""
    ) {
      return null;
    }

    return {
      submittedAt: parsed.submittedAt,
      submittedEmail: parsed.submittedEmail.trim(),
    };
  } catch {
    return null;
  }
}

function pruneValues(values: Partial<FormData>) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (typeof value === "boolean") {
        return value;
      }

      return value !== "";
    }),
  ) as Partial<FormData>;
}

function persistFormState(values: Partial<FormData>, currentStep: number) {
  const payload: PersistedFormState = {
    currentStep,
    values: pruneValues(values),
  };

  document.cookie = `${FORM_COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(payload),
  )}; path=/; max-age=${FORM_COOKIE_MAX_AGE}; SameSite=Lax`;
}

function clearPersistedFormState() {
  document.cookie = `${FORM_COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`;
}

function persistSubmittedFormState(submittedEmail: string) {
  const payload: PersistedSubmissionState = {
    submittedAt: new Date().toISOString(),
    submittedEmail: submittedEmail.trim(),
  };

  document.cookie = `${FORM_SUBMITTED_COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(payload),
  )}; path=/; max-age=${FORM_COOKIE_MAX_AGE}; SameSite=Lax`;
}

function ErrorText({ message = "Pflichtfeld" }: { message?: string }) {
  return <p className="mt-2 text-sm text-red-600">{message}</p>;
}

type LimitedMultiSelectField =
  | "improve"
  | "goal"
  | "successMetrics"
  | "knowledge"
  | "importantPages"
  | "impact"
  | "functionsNeeded";

type OptionListProps = {
  name: keyof FormData;
  options: string[];
  register?: ReturnType<typeof useForm<FormData>>["register"];
  type?: "checkbox" | "radio";
  required?: boolean;
  selectedValues?: string[];
  maxSelections?: number;
  onToggleSelection?: (
    name: LimitedMultiSelectField,
    value: string,
    checked: boolean,
  ) => void;
};

function OptionList({
  name,
  options,
  register,
  type = "checkbox",
  required = false,
  selectedValues = [],
  maxSelections,
  onToggleSelection,
}: OptionListProps) {
  const selectedCount = selectedValues.length;

  return (
    <div className="space-y-3">
      {type === "checkbox" && maxSelections ? (
        <p className="text-sm text-gray-500">
          Max. {maxSelections} Antworten, aktuell {selectedCount}/{maxSelections}.
        </p>
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {options.map((option) => {
          const isChecked = selectedValues.includes(option);
          const isDisabled =
            type === "checkbox" &&
            !!maxSelections &&
            selectedCount >= maxSelections &&
            !isChecked;

          return (
            <label
              key={option}
              className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm transition ${
                isDisabled
                  ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
                  : "border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              {type === "checkbox" ? (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={(event) =>
                    onToggleSelection?.(
                      name as LimitedMultiSelectField,
                      option,
                      event.target.checked,
                    )
                  }
                  className="mt-0.5 h-4 w-4 border-gray-300 text-black"
                />
              ) : (
                <input
                  {...register?.(name, required ? { required: true } : undefined)}
                  type="radio"
                  value={option}
                  className="mt-0.5 h-4 w-4 border-gray-300 text-black"
                />
              )}
              <span>{option}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

export function Fragebogen() {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [showStepError, setShowStepError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const hasRestoredCookie = useRef(false);
  const hasInitializedStepScroll = useRef(false);
  const formTopRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: defaultFormValues,
  });

  const websiteExists = watch("websiteExists");
  const improve = watch("improve") ?? [];
  const goal = watch("goal") ?? [];
  const successMetrics = watch("successMetrics") ?? [];
  const customers = watch("customers");
  const knowledge = watch("knowledge") ?? [];
  const importantPages = watch("importantPages") ?? [];
  const needsExplaining = watch("needsExplaining");
  const impact = watch("impact") ?? [];
  const branding = watch("branding") ?? [];
  const functionsNeeded = watch("functionsNeeded") ?? [];

  const showWebsiteUrl = websiteExists === "Ja";
  const showGoalOther = goal.includes("Sonstiges");
  const showCustomersSpecific = customers === "Spezifische Kundengruppe";
  const showExplainingWhat = needsExplaining === "Ja";

  const progress = ((currentStep + 1) / steps.length) * 100;

  const validateCurrentStep = async () => {
    const stepFields: Array<Array<keyof FormData>> = [
      ["websiteExists", "websiteUrl"],
      [],
      ["customers", "customersSpecific", "ageGroup"],
      ["needsExplaining", "explainingWhat", "assetsAvailability"],
      [],
      [],
      [],
      ["contactName", "contactEmail", "contactCompany", "privacy"],
    ];

    const isValid = await trigger(stepFields[currentStep]);
    setShowStepError(!isValid);
    return isValid;
  };

  const nextStep = async () => {
    const isValid = await validateCurrentStep();
    if (!isValid || currentStep >= steps.length - 1) {
      return;
    }

    setCurrentStep((step) => step + 1);
    setShowStepError(false);
  };

  const prevStep = () => {
    if (currentStep === 0) {
      return;
    }

    setCurrentStep((step) => step - 1);
    setShowStepError(false);
  };

  const onSubmit = async (data: FormData) => {
    setSubmitError("");
    setIsSubmitting(true);

    try {
      await submitQuestionnaire(data as unknown as Record<string, unknown>);
      const nextSubmittedEmail = data.contactEmail.trim();
      persistSubmittedFormState(nextSubmittedEmail);
      setSubmittedEmail(nextSubmittedEmail);
      clearPersistedFormState();
      setIsSubmitted(true);
    } catch (error) {
      if (error instanceof WorkflowApiError) {
        setSubmitError(error.message);
      } else {
        setSubmitError("Der Fragebogen konnte nicht gesendet werden");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleLimitedSelection = (
    name: LimitedMultiSelectField,
    value: string,
    checked: boolean,
    maxSelections?: number,
  ) => {
    const currentValues = (getValues(name) ?? []) as string[];

    if (
      checked &&
      typeof maxSelections === "number" &&
      currentValues.length >= maxSelections
    ) {
      return;
    }

    const nextValues = checked
      ? [...currentValues, value]
      : currentValues.filter((item) => item !== value);

    setValue(name, Array.from(new Set(nextValues)), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const toggleBranding = (value: string, checked: boolean) => {
    const noneValue = "Noch nichts festgelegt";

    if (value === noneValue) {
      setValue("branding", checked ? [noneValue] : [], {
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }

    const nextValues = checked
      ? [...branding.filter((item) => item !== noneValue), value]
      : branding.filter((item) => item !== value);

    setValue("branding", Array.from(new Set(nextValues)), {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    const persistedSubmissionState = readPersistedSubmissionState();

    if (persistedSubmissionState) {
      clearPersistedFormState();
      setSubmittedEmail(persistedSubmissionState.submittedEmail);
      setIsSubmitted(true);
      hasRestoredCookie.current = true;
      return;
    }

    const persistedState = readPersistedFormState();

    if (persistedState) {
      reset({
        ...defaultFormValues,
        ...persistedState.values,
      });
      setCurrentStep(
        Math.min(Math.max(persistedState.currentStep, 0), steps.length - 1),
      );
    }

    hasRestoredCookie.current = true;
  }, [reset]);

  useEffect(() => {
    if (!hasRestoredCookie.current) {
      return;
    }

    const subscription = watch((values) => {
      persistFormState(values as Partial<FormData>, currentStep);
    });

    return () => subscription.unsubscribe();
  }, [currentStep, watch]);

  useEffect(() => {
    if (!hasRestoredCookie.current) {
      return;
    }

    persistFormState(getValues(), currentStep);
  }, [currentStep, getValues]);

  useEffect(() => {
    if (!hasRestoredCookie.current) {
      return;
    }

    if (!hasInitializedStepScroll.current) {
      hasInitializedStepScroll.current = true;
      return;
    }

    const topOffset = formTopRef.current?.getBoundingClientRect().top ?? 0;
    window.scrollTo({
      top: Math.max(window.scrollY + topOffset - 24, 0),
      behavior: "smooth",
    });
  }, [currentStep]);

  useEffect(() => {
    if (!isSubmitted) {
      return;
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [isSubmitted]);

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 px-4 pb-24 pt-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="mb-4 text-4xl font-semibold text-gray-900 sm:text-5xl">
            Vielen Dank
          </h1>
          <p className="mb-8 text-lg leading-relaxed text-gray-600">
            Der Fragebogen wurde erfasst. Eine Empfangsbestaetigung wurde an{" "}
            <span className="font-medium text-gray-900">{submittedEmail}</span>{" "}
            gesendet. Wir melden uns nach Sichtung Ihrer Angaben mit den
            naechsten Schritten.
          </p>
          <div className="rounded-3xl border border-gray-200 bg-white p-8 text-left shadow-sm">
            <h2 className="mb-3 text-xl font-semibold text-gray-900">
              Wie geht es weiter?
            </h2>
            <p className="text-gray-600">
              Ihre Angaben wurden direkt dem internen Workflow zugeordnet.
              Im nächsten Schritt erhalten Sie Ihren Entwurf per E-Mail. Wenn
              Sie bestellen, richten Sie anschließend Ihr Passwort für den
              Kundenbereich ein und übermitteln dort Inhalte oder Zugangsdaten.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 px-4 pb-24 pt-24">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 space-y-3">
          <h1 className="text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
            Website-Fragebogen
          </h1>
          <p className="max-w-2xl text-base leading-relaxed text-gray-600">
            Ihre Antworten helfen, Ziele, Umfang und Prioritäten sauber zu
            klären.
          </p>
        </div>

        <div
          ref={formTopRef}
          className="rounded-[2rem] border border-gray-200 bg-white/95 p-6 shadow-sm backdrop-blur sm:p-8"
        >
          <div className="mb-8 space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-gray-500">
                  Fortschritt
                </div>
                <div className="text-lg font-medium text-gray-900">
                  {currentStep + 1} / {steps.length}
                </div>
                <p className="text-sm text-gray-500">{steps[currentStep].hint}</p>
              </div>
              <p className="text-xs text-gray-500">
                Pflichtfelder werden pro Schritt geprüft.
              </p>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <motion.div
                className="h-full bg-black"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            <ol className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-9">
              {steps.map((step, index) => (
                <li key={step.title}>
                  <button
                    type="button"
                    onClick={() => {
                      setCurrentStep(index);
                      setShowStepError(false);
                    }}
                    className={`flex h-16 w-full flex-col justify-center gap-1 rounded-2xl border px-2 py-2 text-left transition ${
                      index === currentStep
                        ? "border-black bg-black text-white"
                        : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    <span
                      className={`text-[10px] uppercase tracking-wide ${
                        index === currentStep ? "text-gray-300" : "text-gray-500"
                      }`}
                    >
                      Schritt {index + 1}
                    </span>
                    <span className="truncate text-xs font-medium">
                      {step.title}
                    </span>
                  </button>
                </li>
              ))}
            </ol>

            {showStepError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                Bitte füllen Sie die Pflichtfelder im aktuellen Schritt aus.
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-10">
            <AnimatePresence mode="wait">
              {currentStep === 0 && (
                <motion.section
                  key="step-0"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    1. Ausgangslage
                  </h2>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      1. Gibt es aktuell eine Website?
                    </legend>
                    <OptionList
                      name="websiteExists"
                      options={["Nein", "Ja"]}
                      register={register}
                      type="radio"
                      required
                    />
                    {errors.websiteExists && <ErrorText />}

                    {showWebsiteUrl && (
                      <div className="mt-4">
                        <label
                          htmlFor="websiteUrl"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          URL
                        </label>
                        <input
                          id="websiteUrl"
                          {...register("websiteUrl", {
                            required: "Bitte eine URL angeben.",
                            pattern: {
                              value:
                                /^(https?:\/\/)?([A-Za-z0-9-]+\.)+(?:[A-Za-z]{2,24}|xn--[A-Za-z0-9-]{2,59})(\/.*)?$/,
                              message:
                                "Bitte eine gültige Domain angeben, z. B. test.de.",
                            },
                          })}
                          type="text"
                          className={inputClassName}
                          placeholder="test.de"
                        />
                        {errors.websiteUrl && (
                          <ErrorText message={errors.websiteUrl.message} />
                        )}
                      </div>
                    )}
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      2. Falls ja: Was soll sich an der Website verbessern?
                    </legend>
                    <OptionList
                      name="improve"
                      options={improveOptions}
                      selectedValues={improve}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked)
                      }
                    />
                    <div className="mt-4">
                      <label
                        htmlFor="improveOther"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Sonstiges
                      </label>
                      <input
                        id="improveOther"
                        {...register("improveOther")}
                        type="text"
                        className={inputClassName}
                        placeholder="…"
                      />
                    </div>
                  </fieldset>
                </motion.section>
              )}

              {currentStep === 1 && (
                <motion.section
                  key="step-1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    2. Ziel & Erwartung
                  </h2>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      3. Was ist das Hauptziel der Website?
                    </legend>
                    <OptionList
                      name="goal"
                      options={goalOptions}
                      selectedValues={goal}
                      maxSelections={3}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked, 3)
                      }
                    />
                    {showGoalOther && (
                      <div className="mt-4">
                        <label
                          htmlFor="goalOther"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Sonstiges
                        </label>
                        <input
                          id="goalOther"
                          {...register("goalOther", {
                            required: "Bitte kurz ergänzen.",
                          })}
                          type="text"
                          className={inputClassName}
                          placeholder="…"
                        />
                        {errors.goalOther && (
                          <ErrorText message={errors.goalOther.message} />
                        )}
                      </div>
                    )}
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      4. Woran messen Sie, ob Ihre Website erfolgreich ist?
                    </legend>
                    <OptionList
                      name="successMetrics"
                      options={successOptions}
                      selectedValues={successMetrics}
                      maxSelections={2}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked, 2)
                      }
                    />
                    <div className="mt-4">
                      <label
                        htmlFor="successMetricsOther"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Anderes
                      </label>
                      <input
                        id="successMetricsOther"
                        {...register("successMetricsOther")}
                        type="text"
                        className={inputClassName}
                        placeholder="…"
                      />
                    </div>
                  </fieldset>
                </motion.section>
              )}

              {currentStep === 2 && (
                <motion.section
                  key="step-2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    3. Zielgruppe
                  </h2>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      5. Wer sind Ihre typischen Kunden?
                    </legend>
                    <OptionList
                      name="customers"
                      options={[
                        "Privatpersonen",
                        "Unternehmen",
                        "Beides",
                        "Spezifische Kundengruppe",
                      ]}
                      register={register}
                      type="radio"
                      required
                    />
                    {errors.customers && <ErrorText />}

                    {showCustomersSpecific && (
                      <div className="mt-4">
                        <label
                          htmlFor="customersSpecific"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Bitte beschreiben
                        </label>
                        <input
                          id="customersSpecific"
                          {...register("customersSpecific", {
                            required: "Bitte kurz beschreiben.",
                          })}
                          type="text"
                          className={inputClassName}
                          placeholder="…"
                        />
                        {errors.customersSpecific && (
                          <ErrorText message={errors.customersSpecific.message} />
                        )}
                      </div>
                    )}
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      6. Altersgruppe der Zielkunden
                    </legend>
                    <OptionList
                      name="ageGroup"
                      options={ageOptions}
                      register={register}
                      type="radio"
                      required
                    />
                    {errors.ageGroup && <ErrorText />}
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      7. Wie gut kennen sich Ihre Kunden mit Ihrem Thema aus?
                    </legend>
                    <OptionList
                      name="knowledge"
                      options={knowledgeOptions}
                      selectedValues={knowledge}
                      maxSelections={1}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked, 1)
                      }
                    />
                  </fieldset>
                </motion.section>
              )}

              {currentStep === 3 && (
                <motion.section
                  key="step-3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    4. Inhalte & Struktur
                  </h2>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      8. Welche Seiten sind besonders wichtig?
                    </legend>
                    <OptionList
                      name="importantPages"
                      options={pageOptions}
                      selectedValues={importantPages}
                      maxSelections={5}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked, 5)
                      }
                    />
                    <div className="mt-4">
                      <label
                        htmlFor="pagesOther"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Sonstiges
                      </label>
                      <input
                        id="pagesOther"
                        {...register("pagesOther")}
                        type="text"
                        className={inputClassName}
                        placeholder="…"
                      />
                    </div>
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      9. Gibt es Inhalte, die besonders erklärt werden müssen?
                    </legend>
                    <OptionList
                      name="needsExplaining"
                      options={["Nein", "Ja"]}
                      register={register}
                      type="radio"
                      required
                    />
                    {errors.needsExplaining && <ErrorText />}

                    {showExplainingWhat && (
                      <div className="mt-4">
                        <label
                          htmlFor="explainingWhat"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Welche?
                        </label>
                        <input
                          id="explainingWhat"
                          {...register("explainingWhat", {
                            required: "Bitte kurz angeben.",
                          })}
                          type="text"
                          className={inputClassName}
                          placeholder="…"
                        />
                        {errors.explainingWhat && (
                          <ErrorText message={errors.explainingWhat.message} />
                        )}
                      </div>
                    )}
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      10. Sind Texte und Bilder bereits vorhanden?
                    </legend>
                    <OptionList
                      name="assetsAvailability"
                      options={[
                        "Ja, vollständig",
                        "Teilweise",
                        "Nein, Unterstützung gewünscht",
                      ]}
                      register={register}
                      type="radio"
                      required
                    />
                    {errors.assetsAvailability && <ErrorText />}
                  </fieldset>
                </motion.section>
              )}

              {currentStep === 4 && (
                <motion.section
                  key="step-4"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    5. Design & Wirkung
                  </h2>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      11. Wie soll die Website auf Besucher wirken?
                    </legend>
                    <OptionList
                      name="impact"
                      options={impactOptions}
                      selectedValues={impact}
                      maxSelections={2}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked, 2)
                      }
                    />
                  </fieldset>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      12. Gibt es bereits ein Erscheinungsbild?
                    </legend>
                    <div className="grid gap-3 md:grid-cols-2">
                      {[
                        "Logo vorhanden",
                        "Bestimmte Farben / Schriften",
                        "Noch nichts festgelegt",
                      ].map((option) => (
                        <label
                          key={option}
                          className="flex items-start gap-3 rounded-2xl border border-gray-200 px-4 py-3 text-sm text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={branding.includes(option)}
                            onChange={(event) =>
                              toggleBranding(option, event.target.checked)
                            }
                            className="mt-0.5 h-4 w-4 border-gray-300 text-black"
                          />
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <div className={cardClassName}>
                    <label
                      htmlFor="positiveExamples"
                      className="mb-2 block text-base font-medium text-gray-900"
                    >
                      13. Gibt es Designs oder Websites, die Ihnen gefallen?
                    </label>
                    <textarea
                      id="positiveExamples"
                      {...register("positiveExamples")}
                      rows={4}
                      className={textareaClassName}
                      placeholder="Links / Beispiele…"
                    />
                  </div>

                  <div className={cardClassName}>
                    <label
                      htmlFor="negativeExamples"
                      className="mb-2 block text-base font-medium text-gray-900"
                    >
                      14. Gibt es Websites, die keinesfalls als Vorbild dienen
                      sollen?
                    </label>
                    <textarea
                      id="negativeExamples"
                      {...register("negativeExamples")}
                      rows={4}
                      className={textareaClassName}
                      placeholder="Links + kurzer Grund…"
                    />
                  </div>
                </motion.section>
              )}

              {currentStep === 5 && (
                <motion.section
                  key="step-5"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    6. SEO
                  </h2>

                  <div className={cardClassName}>
                    <label
                      htmlFor="seoTerms"
                      className="mb-2 block text-base font-medium text-gray-900"
                    >
                      15. Welche Begriffe würden Ihre Kunden bei Google eingeben?
                    </label>
                    <textarea
                      id="seoTerms"
                      {...register("seoTerms")}
                      rows={4}
                      className={textareaClassName}
                      placeholder="5–10 Stichworte…"
                    />
                  </div>

                  <div className={cardClassName}>
                    <label
                      htmlFor="mostImportantService"
                      className="mb-2 block text-base font-medium text-gray-900"
                    >
                      16. Welche Leistung ist wirtschaftlich am wichtigsten?
                    </label>
                    <textarea
                      id="mostImportantService"
                      {...register("mostImportantService")}
                      rows={3}
                      className={textareaClassName}
                      placeholder="…"
                    />
                  </div>

                  <div className={cardClassName}>
                    <label
                      htmlFor="region"
                      className="mb-2 block text-base font-medium text-gray-900"
                    >
                      17. In welcher Stadt oder Region sollen Sie gefunden werden?
                    </label>
                    <textarea
                      id="region"
                      {...register("region")}
                      rows={2}
                      className={textareaClassName}
                      placeholder="…"
                    />
                  </div>
                </motion.section>
              )}

              {currentStep === 6 && (
                <motion.section
                  key="step-6"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    7. Funktionen
                  </h2>

                  <fieldset className={cardClassName}>
                    <legend className="mb-4 text-base font-medium text-gray-900">
                      18. Welche Funktionen werden benötigt?
                    </legend>
                    <OptionList
                      name="functionsNeeded"
                      options={functionOptions}
                      selectedValues={functionsNeeded}
                      maxSelections={3}
                      onToggleSelection={(name, value, checked) =>
                        toggleLimitedSelection(name, value, checked, 3)
                      }
                    />
                    <div className="mt-4">
                      <label
                        htmlFor="functionsOther"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Sonstiges
                      </label>
                      <input
                        id="functionsOther"
                        {...register("functionsOther")}
                        type="text"
                        className={inputClassName}
                        placeholder="…"
                      />
                    </div>
                  </fieldset>
                </motion.section>
              )}

              {currentStep === 7 && (
                <motion.section
                  key="step-7"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h2 className="text-2xl font-semibold text-gray-900">
                    8. Abschluss
                  </h2>

                  <div className={cardClassName}>
                    <label
                      htmlFor="notes"
                      className="mb-2 block text-base font-medium text-gray-900"
                    >
                      19. Gibt es darüber hinaus Wünsche, Bedenken oder
                      Besonderheiten?
                    </label>
                    <textarea
                      id="notes"
                      {...register("notes")}
                      rows={6}
                      className={textareaClassName}
                      placeholder="…"
                    />
                  </div>

                  <div className={cardClassName}>
                    <div className="mb-4">
                      <h3 className="text-base font-medium text-gray-900">
                        20. Kontaktdaten
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-gray-600">
                        Damit wir uns mit Rückfragen oder einem Angebot bei Ihnen
                        melden können.
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label
                          htmlFor="contactName"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Name
                        </label>
                        <input
                          id="contactName"
                          {...register("contactName", {
                            required: "Bitte Ihren Namen angeben.",
                          })}
                          type="text"
                          className={inputClassName}
                          placeholder="Ihr Name"
                        />
                        {errors.contactName && (
                          <ErrorText message={errors.contactName.message} />
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="contactEmail"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          E-Mail
                        </label>
                        <input
                          id="contactEmail"
                          {...register("contactEmail", {
                            required: "Bitte eine E-Mail-Adresse angeben.",
                            pattern: {
                              value: /^\S+@\S+\.\S+$/,
                              message: "Bitte eine gültige E-Mail eingeben.",
                            },
                          })}
                          type="email"
                          className={inputClassName}
                          placeholder="ihre@email.de"
                        />
                        {errors.contactEmail && (
                          <ErrorText message={errors.contactEmail.message} />
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="contactPhone"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Telefon
                        </label>
                        <input
                          id="contactPhone"
                          {...register("contactPhone")}
                          type="tel"
                          className={inputClassName}
                          placeholder="+49 …"
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="contactCompany"
                          className="mb-2 block text-sm font-medium text-gray-700"
                        >
                          Unternehmen
                        </label>
                        <input
                          id="contactCompany"
                          {...register("contactCompany", {
                            required: "Bitte Ihr Unternehmen angeben.",
                          })}
                          type="text"
                          className={inputClassName}
                          placeholder="…"
                        />
                        {errors.contactCompany && (
                          <ErrorText message={errors.contactCompany.message} />
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6">
                    <h3 className="text-base font-medium text-gray-900">
                      Wie geht es weiter?
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                      Nach dem Absenden sichten wir Ihre Antworten und melden uns
                      mit Angebot oder Ansichtsbeispiel.
                    </p>
                  </div>

                  <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
                    <label className="flex items-start gap-3 text-sm leading-relaxed text-gray-600">
                      <input
                        {...register("privacy", {
                          required: "Bitte stimmen Sie der Datenverarbeitung zu.",
                        })}
                        type="checkbox"
                        className="mt-1 h-4 w-4 border-gray-300 text-black"
                      />
                      <span>
                        Ich habe die{" "}
                        <Link
                          to="/datenschutz"
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-gray-900 underline underline-offset-4"
                          onClick={(event) => event.stopPropagation()}
                        >
                          Datenschutzerklärung
                        </Link>{" "}
                        zur Kenntnis genommen und stimme der Verarbeitung meiner
                        Daten zur Bearbeitung des Fragebogens zu.
                      </span>
                    </label>
                    {errors.privacy && (
                      <ErrorText message={errors.privacy.message as string} />
                    )}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>

            <div className="space-y-3 pt-2">
              {submitError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {submitError}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 0}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                  Weiter
                  <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-medium text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <CheckCircle className="h-4 w-4" />
                  {isSubmitting ? "Wird gesendet..." : "Fragebogen absenden"}
                </button>
              )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
